import React from 'react';
import PropTypes from 'prop-types';
import './review.css';

// Frozen module-level singletons for empty default props (#943,
// react/no-object-type-as-default-prop). A `= []` / `= {}` default builds a NEW
// value on every render, so a memoised child or an effect dependency sees a change
// that never happened.
const EMPTY_OBJECT = Object.freeze({});

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'open', label: 'Open' },
  { key: 'unapproved', label: 'Unapproved' },
  { key: 'stale', label: 'Stale' },
];

/**
 * Review header — ported 1:1 from the mock's `.revhead` (docs/design/specimen/
 * review.html, #450): row 1 = `.prog` progress bar + `.legend` (a dot + count per
 * approval bucket, "stale" only shown when >0, then "· N open"); row 2 = the
 * `.disp` attributes-toggle switch + the `.seg` All/Open/Unapproved/Stale filter.
 *
 * Row 1 is `role="status" aria-live="polite"` (spec Accessibility: "All counts
 * announced via aria-live=polite"). The filter segment is `role="tablist"` with
 * `role="tab"`/`aria-selected` options — unchanged contract from the pre-restyle
 * version, just rendered as plain elements instead of MUI Box/sx.
 *
 * @param {object} props
 * @param {{
 *   approved?: number,
 *   stale?: number,
 *   not_approved?: number,
 *   pending?: number,
 *   open_comments?: number,
 * }} [props.rollup] Per-bucket counts; every bucket defaults to 0 when absent.
 * @param {string} props.filter
 * @param {(next: string) => void} props.onFilterChange
 * @param {boolean} props.showAttributes
 * @param {() => void} props.onToggleAttributes
 */
function ReviewHeader({ rollup = EMPTY_OBJECT, filter, onFilterChange, showAttributes, onToggleAttributes }) {
  const approved = rollup.approved || 0;
  const stale = rollup.stale || 0;
  const notApproved = rollup.not_approved || 0;
  const pending = rollup.pending || 0;
  const openComments = rollup.open_comments || 0;
  const total = approved + stale + notApproved + pending || 1;

  return (
    <div className="revhead" data-testid="review-header">
      <div className="revrow" role="status" aria-live="polite">
        <div className="prog" aria-hidden="true">
          <i style={{ width: `${(approved / total) * 100}%`, background: 'var(--success)' }} />
          <i style={{ width: `${(stale / total) * 100}%`, background: 'var(--warning)' }} />
          <i style={{ width: `${(notApproved / total) * 100}%`, background: 'var(--error)' }} />
        </div>
        <div className="legend" data-testid="review-metrics">
          <span><span className="dot success" /> <b>{approved}</b> approved</span>
          {' '}
          {stale > 0 && <span><span className="dot warning" /> <b>{stale}</b> stale</span>}
          {stale > 0 && ' '}
          <span><span className="dot error" /> <b>{notApproved}</b> not approved</span>
          {' '}
          <span><span className="dot muted" /> <b>{pending}</b> pending</span>
          {' '}
          <span>
            ·
            {' '}
            <b>{openComments}</b>
            {' '}
            open
          </span>
        </div>
      </div>
      <div className="revrow">
        <button
          type="button"
          className="disp"
          onClick={onToggleAttributes}
          aria-pressed={showAttributes}
          aria-label="Show item attributes"
        >
          <span className={`switch${showAttributes ? '' : ' off'}`}><i /></span>
          Attributes
        </button>
        <div className="grow" />
        {/* `.seg` (ds.css) — the same segmented-control look used elsewhere in the
            app and by the mock's `#filterseg` (docs/design/specimen/review.html,
            #450); role/aria-selected preserved for a11y + the existing test
            contract (role="tablist"/"tab"). */}
        <div role="tablist" aria-label="Breakdown filter" className="seg">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              role="tab"
              aria-selected={filter === f.key}
              onClick={() => onFilterChange(f.key)}
              className={filter === f.key ? 'on' : undefined}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

ReviewHeader.propTypes = {
  rollup: PropTypes.shape({
    approved: PropTypes.number,
    stale: PropTypes.number,
    not_approved: PropTypes.number,
    pending: PropTypes.number,
    open_comments: PropTypes.number,
  }),
  filter: PropTypes.oneOf(['all', 'open', 'unapproved', 'stale']).isRequired,
  onFilterChange: PropTypes.func.isRequired,
  showAttributes: PropTypes.bool.isRequired,
  onToggleAttributes: PropTypes.func.isRequired,
};

export default ReviewHeader;
